import clsx from 'clsx'
import { useMemo } from 'react'

interface AvatarProps {
  variant?: string
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | '2xl'
  className?: string
  src?: string
  name?: string
  bgColor?: string
  color?: string
}

const AvatarSizes = {
  xs: '24px',
  s: '32px',
  m: '40px',
  l: '56px',
  xl: '72px',
  xxl: '120px',
  '2xl': '250px',
}

const randomAvatarBgColor = ['#22d3ee', '#2dd4bf', '#8393a5', '#c880b2', '#d67e84', '#f87171', '#7b90df', '#facc15']

// Deterministically derive a color from the name so it stays stable across
// re-renders (e.g. when a row is expanded/collapsed). Falls back to a single
// random pick when there is no name to derive from.
const getAvatarBgColor = (name?: string) => {
  if (!name) return randomAvatarBgColor[Math.floor(Math.random() * randomAvatarBgColor.length)]

  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return randomAvatarBgColor[hash % randomAvatarBgColor.length]
}

export default function Avatar({
  variant = 'circle',
  size,
  className,
  src,
  name,
  bgColor,
  color = '#fff',
}: AvatarProps) {
  const resolvedBgColor = useMemo(() => bgColor ?? getAvatarBgColor(name), [bgColor, name])

  return (
    <>
      {variant === 'circle' && (
        <div
          className={clsx('rounded-full flex justify-center items-center', className)}
          style={{
            backgroundColor: resolvedBgColor,
            color: color,
            width: AvatarSizes[size || 'm'],
            height: AvatarSizes[size || 'm'],
          }}
        >
          {name && !src && <span className="text-[14px] font-bold">{name?.charAt(0).toUpperCase()}</span>}
          {src && <img src={src} alt={name} className="w-full h-full object-cover rounded-full" />}
        </div>
      )}
      {variant === 'square' && (
        <div
          className={clsx('rounded-[38px] text-[72px] flex justify-center items-center', className)}
          style={{
            backgroundColor: resolvedBgColor,
            color: color,
            width: AvatarSizes[size || 'm'],
            height: AvatarSizes[size || 'm'],
          }}
        >
          {name && <span>{name?.charAt(0).toUpperCase()}</span>}
          {src && <img src={src} alt={name} className="w-full h-full object-cover rounded" />}
        </div>
      )}
    </>
  )
}
